import { useState } from 'react';
import { motion } from 'framer-motion';
import AnimatedPage from '../components/AnimatedPage';
import { sendContactForm } from '../services/web3forms';
import toast from 'react-hot-toast';
import { Mail, MessageSquare, User, Send } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    const result = await sendContactForm(formData);
    setLoading(false);

    if (result.success) {
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } else {
      toast.error('Failed to send message. Please try again.');
    }
  };

  const inputClass =
    'w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all font-body';

  return (
    <AnimatedPage>
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="heading-1 mb-6">Get In Touch</h1>
              <div className="w-20 h-1 bg-black mx-auto mb-8" />
              <p className="body-text text-gray-600">
                Have a question, suggestion, or just want to say hello? We'd love to
                hear from you. Fill out the form below and we'll get back to you as
                soon as possible.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Form & Info */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Form */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg"
              >
                <h2 className="heading-3 mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-body font-medium mb-2">
                      Name *
                    </label>
                    <div className="relative">
                      <User
                        size={20}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`${inputClass} pl-12`}
                        placeholder="Your name"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-body font-medium mb-2">
                      Email *
                    </label>
                    <div className="relative">
                      <Mail
                        size={20}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`${inputClass} pl-12`}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-body font-medium mb-2">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={inputClass}
                      placeholder="What's this about?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-body font-medium mb-2">
                      Message *
                    </label>
                    <div className="relative">
                      <MessageSquare
                        size={20}
                        className="absolute left-3 top-3 text-gray-400"
                      />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="6"
                        className={`${inputClass} pl-12 resize-none`}
                        placeholder="Your message..."
                        required
                      />
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-4 bg-black text-white rounded-lg font-body font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send size={18} className="ml-2" />
                      </>
                    )}
                  </motion.button>
                </form>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="heading-3 mb-6">Contact Information</h2>
                  <p className="body-text text-gray-600 mb-8">
                    Feel free to reach out through any of these channels. We're here
                    to help and would love to connect with you.
                  </p>
                </div>

                <div className="space-y-6">
                  <motion.div
                    whileHover={{ x: 5 }}
                    className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="w-12 h-12 bg-black text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h3 className="font-body font-semibold mb-1">Email</h3>
                      <a
                        href="mailto:contact@raiblog.com"
                        className="text-gray-600 hover:text-black transition-colors font-body"
                      >
                        raimohan9676@gmail.com
                      </a>
                    </div>
                  </motion.div>

                  <motion.div
                    whileHover={{ x: 5 }}
                    className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="w-12 h-12 bg-black text-white rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageSquare size={24} />
                    </div>
                    <div>
                      <h3 className="font-body font-semibold mb-1">Social Media</h3>
                      <p className="text-gray-600 font-body">
                        Follow us on Twitter, LinkedIn, and GitHub
                      </p>
                    </div>
                  </motion.div>
                </div>

                <div className="bg-black text-white p-8 rounded-2xl">
                  <h3 className="heading-4 mb-4">Response Time</h3>
                  <p className="text-gray-300 font-body">
                    We typically respond to all inquiries within 24-48 hours during
                    business days. Thank you for your patience!
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
    </AnimatedPage>
  );
};

export default Contact;
